import { createContext, lazy, Suspense, useContext, useEffect, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { setMini, useMiniPlayer, type WatchLocation } from '@/lib/mini-player';
import { appPages } from '@/lib/preload';
import { PlayerSkeleton } from '../skeletons';

const loadWatch = () => import('@/routes/watch');
appPages.push(loadWatch);
const WatchPage = lazy(loadWatch);

const WATCH_PATHS = ['/watch/movie/:id', '/watch/series/:id/:season/:episode'];
export const watchParams = (pathname: string) => {
  for (const p of WATCH_PATHS) {
    const m = matchPath(p, pathname);
    if (m) return m.params as { id?: string; season?: string; episode?: string };
  }
  return null;
};

interface WatchRoute {
  loc: WatchLocation;
  /** Playing in the corner while the rest of the app is used. */
  mini: boolean;
  /** Moves the player to another watch address: the app's address when full, its own when mini. */
  go: (to: string, opts?: { replace?: boolean; state?: unknown }) => void;
  /** Closes the player (when mini) and opens a page of the app. */
  leave: (to: string, opts?: { replace?: boolean }) => void;
  toMini: (back: string) => void;
  expand: () => void;
  close: () => void;
}

const Ctx = createContext<WatchRoute | null>(null);
export function useWatchRoute() {
  return useContext(Ctx)!;
}

/**
 * The player lives here, beside the pages rather than in one, so it can shrink into a mini player
 * and keep playing (the same video, nothing reloads) while you browse, then grow back. It's drawn
 * full size on a watch address, or in the corner over everything else while the mini player is on.
 */
export function PlayerHost() {
  const location = useLocation();
  const navigate = useNavigate();
  const mini = useMiniPlayer();
  const onWatch = !!watchParams(location.pathname);

  // Opening a watch page (expanding, or playing something else) ends the mini player. Not the
  // one it was made from, which is still the address until the way back opens.
  const madeAt = useRef<string | null>(null);
  useEffect(() => {
    if (onWatch && mini && location.key !== madeAt.current) setMini(null);
  }, [onWatch, mini, location.key]);
  // Closed with the app's pages, so nothing keeps playing behind a sign-in or profile screen.
  useEffect(() => () => setMini(null), []);

  const loc: WatchLocation | null = onWatch
    ? { pathname: location.pathname, search: location.search, state: location.state }
    : mini;
  if (!loc) return null;
  const isMini = !onWatch;

  const value: WatchRoute = {
    loc,
    mini: isMini,
    go: (to, opts = {}) => {
      if (!isMini) return void navigate(to, opts);
      const url = new URL(to, window.location.origin);
      setMini({ pathname: url.pathname, search: url.search, state: opts.state ?? null });
    },
    leave: (to, opts) => {
      setMini(null);
      navigate(to, opts);
    },
    toMini: (back) => {
      madeAt.current = location.key;
      setMini(loc);
      navigate(back, { replace: true });
    },
    expand: () => navigate(`${loc.pathname}${loc.search}`, { state: loc.state }),
    close: () => setMini(null),
  };

  return (
    <Ctx.Provider value={value}>
      <div
        data-mini-player={isMini || undefined}
        className={
          isMini
            ? // Over every page, panel and popup; above the phone tab bar.
              'pointer-events-auto fixed bottom-[max(5.5rem,calc(env(safe-area-inset-bottom)+5rem))] right-3 z-[1000] aspect-video w-[min(22rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_30px_80px_-20px_rgba(0,0,0,.95)] animate-[rise_250ms_var(--ease-out-soft)] md:bottom-6 md:right-6 md:w-[26rem]'
            : undefined
        }
      >
        <Suspense fallback={isMini ? null : <PlayerSkeleton />}>
          <WatchPage key={loc.pathname} />
        </Suspense>
      </div>
    </Ctx.Provider>
  );
}
